// hooks/useProjectsWithThreadsRealtime.ts
import * as React from "react";
import {
  Firestore, collection, query, where, orderBy, onSnapshot, limit as qLimit, DocumentData
} from "firebase/firestore";

// Configure one of these:
// 1) If you made thumbs public:
const GCS_PUBLIC_BASE = "https://storage.cloud.google.com/" + (process.env.REACT_APP_BUCKET_NAME || 'ai_conic_history');

const asDate = (v: any): Date => v?.toDate?.() ?? new Date(0);

function objectPathToPublicUrl(objectPath?: string | null): string | null {
  if (!objectPath) return null;
  if (!GCS_PUBLIC_BASE) return null;
  // ensure no leading slash
  const obj = objectPath.startsWith("/") ? objectPath.slice(1) : objectPath;
  return `${GCS_PUBLIC_BASE.replace(/\/+$/, "")}/${obj}`;
}

export function useProjectsWithThreadsRealtime(
  db: Firestore,
  uid: string | undefined,
  perProjectLimit = 50
) {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  
  // Store project names to ensure we always have the latest
  const projectNamesRef = React.useRef<Map<string, string>>(new Map());
  // Track which projects are currently active/accessible
  const activeProjectsRef = React.useRef<Set<string>>(new Set());
  // Track which projects have loaded their initial thread data
  const loadedProjectsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (!uid) return;
    setLoading(true);
    setError(null);

    const threadUnsubs = new Map<string, () => void>();

    // Capture ref values for cleanup at effect setup time
    const projectNamesAtSetup = projectNamesRef.current;
    const activeProjectsAtSetup = activeProjectsRef.current;
    const loadedProjectsAtSetup = loadedProjectsRef.current;

    const projQ = query(
      collection(db, "projects"),
      where("user_ids", "array-contains", uid),
      orderBy("created_at", "asc")
    );

    const unsubProjects = onSnapshot(
      projQ,
      (projSnap) => {
        const live = new Set(projSnap.docs.map((d) => d.id));
        
        // Update active projects ref
        activeProjectsRef.current = live;

        // Update project names from snapshot
        projSnap.docs.forEach((pDoc) => {
          const project_id = pDoc.id;
          const project_name = (pDoc.get("name") as string) ?? project_id;
          projectNamesRef.current.set(project_id, project_name);
        });

        // cleanup removed projects
        for (const [pid, unsub] of threadUnsubs) {
          if (!live.has(pid)) {
            unsub();
            threadUnsubs.delete(pid);
            projectNamesRef.current.delete(pid);
            loadedProjectsRef.current.delete(pid);
          }
        }
        
        // Remove inaccessible projects from state immediately
        setProjects((prev) => prev.filter((p) => live.has(p.project_id)));

        // If no projects exist, set loading to false immediately
        if (projSnap.empty) {
          setProjects([]);
          setLoading(false);
          return;
        }

        // ensure a threads listener per project
        projSnap.docs.forEach((pDoc) => {
          const project_id = pDoc.id;

          if (threadUnsubs.has(project_id)) return;

          const threadsQ = query(
            collection(db, "projects", project_id, "threads"),
            orderBy("updated_at", "desc"),
            qLimit(perProjectLimit)
          );

          const unsub = onSnapshot(
            threadsQ,
            (thrSnap) => {
              // Safety check: only update if project is still active
              if (!activeProjectsRef.current.has(project_id)) {
                return;
              }

              const rows: Thread[] = thrSnap.docs.map((t) => {
                const d = t.data() as DocumentData;

                // Prefer a full URL written by the backend; otherwise derive from object path
                const derived = objectPathToPublicUrl(d?.thumbnail_object as string | undefined);
                const thumbnail_url = derived ?? null;

                return {
                  thread_id: t.id,
                  name: (d?.name as string) ?? t.id,
                  length: (d?.length as number) ?? 0,
                  created_at: asDate(d?.created_at),
                  updated_at: asDate(d?.updated_at),
                  thumbnail_url: thumbnail_url ?? undefined,
                };
              });

              // Get fresh project name from ref
              const project_name = projectNamesRef.current.get(project_id) ?? project_id;

              // update this project's threads
              setProjects((prev) => {
                const map = new Map(prev.map((p) => [p.project_id, p]));
                // Always create a new object to ensure React detects the change
                map.set(project_id, { 
                  project_id, 
                  project_name, 
                  threads: rows 
                });
                // optional: sort projects by name
                return Array.from(map.values()).sort((a, b) =>
                  a.project_name.localeCompare(b.project_name)
                );
              });

              // Mark this project as loaded
              loadedProjectsRef.current.add(project_id);

              // Check if all active projects have loaded
              const allProjectsLoaded = Array.from(activeProjectsRef.current).every(
                pid => loadedProjectsRef.current.has(pid)
              );

              if (allProjectsLoaded) {
                setLoading(false);
              }
            },
            (err) => {
              setError(err.message || String(err));
              setLoading(false);
            }
          );

          threadUnsubs.set(project_id, unsub);
        });
      },
      (err) => {
        setError(err.message || String(err));
        setLoading(false);
      }
    );

    return () => {
      unsubProjects();
      for (const unsub of threadUnsubs.values()) unsub();
      threadUnsubs.clear();

      projectNamesAtSetup.clear();
      activeProjectsAtSetup.clear();
      loadedProjectsAtSetup.clear();
    };
  }, [db, uid, perProjectLimit]);

  return { projects, loading, error };
}
